<template>
  <div class="sale-details">
    <div
      class="d-flex flex-lg-row flex-column justify-content-between gap-4 mb-4"
    >
      <h5 class="text-semibold">
        Detalhes da venda
      </h5>

      <div class="d-flex flex-wrap gap-2">
        <snow-button
          v-if="$permissions.hasPermission('reenviar-notificacoes')"
          text="Reenviar POST"
          variant="outline"
          :disabled="loadingPost"
          @click="resendPost"
        />

        <snow-button
          v-if="$permissions.hasPermission('alterar-e-mail-do-comprador')"
          text="Trocar e-mail"
          variant="outline"
          @click="changeEmailDialog = true"
        />
        <snow-button
          v-if="showChangeCard"
          variant="outline"
          text="Trocar cartão"
          @click="changeCard"
        />
      </div>
    </div>

    <div v-if="loading" class="text-center my-9">
      <b-spinner variant="primary" />
    </div>

    <template v-else>
      <div class="d-flex flex-lg-row flex-column gap-4 w-100">
        <div class="card-gray">
          <h5 class="text-semibold mb-4">Dados do cliente</h5>
          <div class="info-grid">
            <span class="label">Nome</span>
            <span class="value">{{ sale.shopper_name }}</span>

            <span class="label">Email</span>
            <span class="value">{{ sale.shopper_email }}</span>

            <span class="label">Telefone</span>
            <span class="value">{{ sale.shopper_phone }}</span>

            <span class="label">Documento de identificação</span>
            <span class="value">{{ sale.shopper_document }}</span>

            <template v-if="sale.instagram">
              <span class="label">Instagram</span>
              <span class="value">{{ sale.instagram }}</span>
            </template>
          </div>
        </div>
        <div class="card-gray">
          <h5 class="text-semibold mb-4">Informações da compra</h5>

          <div class="info-grid">
            <template v-if="saleProduct">
              <span class="label">Produto</span>
              <span class="value">{{ saleProduct.name }}</span>
            </template>

            <span class="label">Oferta</span>
            <span class="value">{{
              sale.offer_name || "Oferta principal"
            }}</span>

            <span class="label">Código da transação</span>
            <span class="value">{{ code }}</span>

            <span class="label">Status da transação</span>
            <span class="value">
              <span :class="getStatusBadge(sale)">
                {{ sale.status | formatStatusName }}
              </span>
            </span>

            <template v-if="$permissions.hasPermission('visualizar-valores')">
              <span class="label">Valor da compra</span>
              <span class="value">
                R$ {{ formatMoney(sale.amount) }}
                (
                {{ getPayMethod(sale.payment_method) }}
                )
              </span>
            </template>

            <span class="label">Parcelas</span>
            <span class="value">{{ sale.installments }}</span>

            <span class="label">Data da compra</span>
            <span class="value">{{ createdAt }}</span>

            <span class="label">Data de aprovação</span>
            <span class="value">{{ paidAt }}</span>

            <template
              v-if="
                sale.charge_data &&
                  sale.charge_data.cards &&
                  sale.charge_data.cards.length
              "
            >
              <span class="label">Mensagem</span>
              <span class="value">{{ lastCard.acquirer_message }}</span>

              <span class="label">Últimos 4 dígitos do cartão</span>
              <span class="value">{{ lastCard.last_four_digits || "-" }}</span>
            </template>

            <span class="label">Link do pagamento</span>
            <span class="value d-flex align-items-center gap-1">
              <div class="ellipsis">
                {{ sale.shopper_offer_link }}
              </div>
              <snow-button
                variant="borderless"
                size="sm"
                icon="ph ph-copy"
                @click="copyLink"
              />
            </span>

            <template v-if="sale.token_subscription">
              <span class="label">Link para troca do cartão</span>
              <span class="value d-flex align-items-center gap-1">
                <div class="ellipsis">
                  {{ sale.token_subscription.link }}
                </div>
                <snow-button
                  variant="borderless"
                  size="sm"
                  icon="ph ph-copy"
                  @click="copyChangeLink"
                />
              </span>
            </template>

            <span class="label">Origem de checkout (sck)</span>
            <span class="value">{{ sale.sck || "-" }}</span>

            <span class="label">Order Bump</span>
            <span class="value">
              <span
                :class="`${sale.is_order_bump ? 'active' : 'inactive'}-badge`"
              >
                {{ sale.is_order_bump ? "Sim" : "Não" }}
              </span>
            </span>

            <span class="label">Upsell</span>
            <span class="value">
              <span :class="`${sale.is_upsell ? 'active' : 'inactive'}-badge`">
                {{ sale.is_upsell ? "Sim" : "Não" }}
              </span>
            </span>
          </div>
        </div>
      </div>
    </template>

    <modal-completed
      v-model="completed.modal"
      title="Concluído"
      :description="completed.text"
    />

    <responsive-modal v-model="changeEmailDialog" title="Trocar e-mail">
      <change-email :id="sale.id" @close="changeEmailDialog = false" />
    </responsive-modal>
  </div>
</template>

<script>
import ChangeEmail from "@/components/sale/ChangeEmail.vue";

import { formatMoney } from "@/functions/helpers";
import { first, get } from "lodash";
import { mapState } from "vuex";
import SaleMixin from "@/mixins/sale";

const formatDate = dateStr => {
  if (!dateStr) {
    return "-";
  }
  let arr = dateStr.split("T");
  if (arr.length < 2) {
    return "-";
  }
  return (
    arr[0]
      .split("-")
      .reverse()
      .join("/") +
    " às " +
    arr[1].substr(0, 5)
  );
};

export default {
  components: { ChangeEmail },
  mixins: [SaleMixin],
  data() {
    return {
      loading: true,
      loadingCard: false,
      loadingPost: false,
      changeEmailDialog: false
    };
  },
  computed: {
    ...mapState("product", ["products"]),
    ...mapState("sale", ["sale"]),
    saleProduct() {
      return this.products.find(p => p.id === this.sale?.product);
    },
    lastCard() {
      return first(get(this.sale, "charge_data.cards", []));
    },
    showChangeCard() {
      return (
        this.sale.charge_type === "recurrent" ||
        this.sale.charge_type === "signature"
      );
    },
    code() {
      return this.sale && this.sale.shopper_code
        ? this.sale.shopper_code.slice(0, 8).toUpperCase()
        : "";
    },
    createdAt() {
      return formatDate(this.sale.created_at);
    },
    paidAt() {
      return this.sale.status === "failed"
        ? "-"
        : formatDate(this.sale.paid_at);
    }
  },
  methods: {
    formatMoney,
    copyLink() {
      let link = this.sale.shopper_offer_link;
      if (link) {
        navigator.clipboard.writeText(link);
        this.$message.success("Link copiado");
      }
    },
    copyChangeLink() {
      let link = this.sale.token_subscription.link;
      if (link) {
        navigator.clipboard.writeText(link);
        this.$message.success("Link copiado");
      }
    },
    changeCard() {
      this.loadingCard = true;
      this.$store
        .dispatch("subscriptions/requestChangeCard", this.sale.id)
        .then(() => {
          this.$message.success(
            "Um e-mail foi enviado ao cliente com um link para alterar os dados do cartão"
          );
        })
        .catch(() => {
          this.$message.error("Erro");
        })
        .finally(() => {
          this.loadingCard = false;
        });
    },
    resendPost() {
      this.loadingPost = true;
      window.axios
        .post(`/gateways/order/resend-event/${this.sale.id}`)
        .then(() => {
          this.$message.success("Evento reenviado com sucesso");
        })
        .catch(error => {
          this.$message.error(
            get(error, "response.data.message", "Erro ao reenviar evento")
          );
        })
        .finally(() => {
          this.loadingPost = false;
        });
    }
  },
  async mounted() {
    const id = this.$route.params.id;
    if (!id) return;

    this.loading = true;

    await Promise.all([
      this.$store.dispatch("sale/getSale", id),
      !this.products.length &&
        this.$store.dispatch("product/get", { limit: 1000 })
    ]);

    this.loading = false;
  }
};
</script>
<style lang="scss" scoped>
.sale-details {
  .card-gray {
    flex: 1;
    background: var(--bg-2);
    padding: 24px;
    border-radius: 16px;
  }
  .info-grid {
    display: grid;
    grid-template-columns: minmax(150px, 200px) 1fr;
    row-gap: 16px;
    column-gap: 24px;
    @media screen and (max-width: 991px) {
      grid-template-columns: minmax(100px, 150px) 1fr;
    }
  }

  .label {
    color: var(--dark-40);
    font-weight: 500;
    font-size: 12px;
  }

  .value {
    font-size: 12px;
    color: var(--dark);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
